/* dl-vimeo.js (reduced false fallback + min quality 720p + captions off)
 * - Fallback only on critical errors
 * - No fallback on autoplay/play/pause errors
 * - Ready watchdog to tolerate slow loads
 * - Lazy mount via IntersectionObserver (added)
 * - Minimum quality enforced to 720p (via URL param)
 * - Captions always off (retry & after video load)
 */

(function () {
  // ============== Vimeo API Loader with relaxed timeout ==============
  function loadVimeoAPI(timeoutMs = 15000) {
    return new Promise((resolve, reject) => {
      if (window.Vimeo && window.Vimeo.Player) return resolve();
      const src = "https://player.vimeo.com/api/player.js";
      let exist = document.querySelector(`script[src="${src}"]`);

      let tid = setTimeout(() => {
        reject(new Error("Vimeo API load timeout"));
      }, timeoutMs);

      function done() {
        clearTimeout(tid);
        resolve();
      }
      function fail() {
        clearTimeout(tid);
        reject(new Error("Vimeo API load error"));
      }

      if (!exist) {
        const tag = document.createElement("script");
        tag.src = src;
        tag.async = true;
        tag.onload = done;
        tag.onerror = fail;
        document.head.appendChild(tag);
      } else {
        if (exist.readyState === "complete") return done();
        exist.addEventListener("load", done, { once: true });
        exist.addEventListener("error", fail, { once: true });
      }
    });
  }

  class DL_Vimeo extends HTMLElement {
    constructor() {
      super();
      this._onScroll = this.onScroll.bind(this);
      this._onResize = this.onResize.bind(this);
      this._onCoverClick = this.onCoverClick.bind(this);

      this._ready = false;
      this._ro = null;
      this._io = null; // 初回生成のためのIO
      this._playIo = null; // 既存コードで使う場合に備えて保持
      this._coverEl = null;
      this._fallbackShown = false;
      this._readyWatchdogId = null;
      this._initStarted = false; // 二重init防止
      this._aspectRatioSet = false; // アスペクト比設定済みフラグ

      this.player = null;
      this.idVideo = null;
      this.mode = "autoplay"; // "autoplay" | "interactive"
    }

    connectedCallback() {
      window.addEventListener("scroll", this._onScroll, { passive: true });
      window.addEventListener("resize", this._onResize);

      // ❶ すでに画面内にある場合は「遅延なしで即初期化」
      if (this._isInViewNow(1) && !this._initStarted) {
        this._initStarted = true;
        loadVimeoAPI()
          .then(() => this.init())
          .catch((e) => {
            console.warn("[DL_Vimeo] Vimeo API error:", e);
            this._criticalFallback("api-load");
          });
        return; // IOは不要
      }

      // ❷ ±1画面（上下1ビューポート分）に入ったら初期化
      try {
        if (this._io && this._io.disconnect) this._io.disconnect();
      } catch (e) {
        console.warn("Vimeo player error:", e);
      }
      this._io = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            if (this._initStarted) return;
            this._initStarted = true;
            this._io.unobserve(this);

            loadVimeoAPI()
              .then(() => this.init())
              .catch((e) => {
                console.warn("[DL_Vimeo] Vimeo API error:", e);
                this._criticalFallback("api-load");
              });
          });
        },
        {
          // 上下に「1画面ぶん」の余白。左右は0px。
          // 例: 画面の下端から1画面上にある要素もヒットする
          rootMargin: "100% 0px 100% 0px",
          threshold: 0.01,
        }
      );
      this._io.observe(this);
    }

    disconnectedCallback() {
      window.removeEventListener("scroll", this._onScroll);
      window.removeEventListener("resize", this._onResize);

      if (this._ro) {
        try {
          this._ro.disconnect();
        } catch (e) {
          console.warn("Vimeo player error:", e);
        }
      }
      this._ro = null;

      if (this._io) {
        try {
          this._io.disconnect();
        } catch (e) {
          console.warn("Vimeo player error:", e);
        }
      }
      this._io = null;

      // イベントリスナの確実な削除
      try {
        this.removeEventListener("click", this._onCoverClick);
      } catch (e) {
        console.warn("Vimeo player error:", e);
      }
      if (this._coverEl) {
        try {
          this._coverEl.removeEventListener("click", this._onCoverClick);
        } catch (e) {
          console.warn("Vimeo player error:", e);
        }
      }

      if (this._readyWatchdogId) {
        clearTimeout(this._readyWatchdogId);
        this._readyWatchdogId = null;
      }

      if (this.player) {
        try {
          if (this.player.off) {
            this.player.off("ended");
            this.player.off("pause");
            this.player.off("play");
            this.player.off("error");
            this.player.off("loaded"); // 追加: 後始末
          }
          if (this.player.unload) this.player.unload().catch(() => {});
        } catch (e) {
          console.warn("Vimeo player error:", e);
        }
      }
      this.player = null;
      this._coverEl = null;
      this._ready = false;
      this._initStarted = false;
    }

    // ========= utils =========
    _media() {
      try {
        return typeof DL_media === "function" ? DL_media() : innerWidth <= 680 ? "SP" : "PC";
      } catch {
        return innerWidth <= 680 ? "SP" : "PC";
      }
    }
    _bool(v, def = false) {
      return v == null ? def : Boolean(JSON.parse(String(v)));
    }
    _aspectFallback() {
      const attr = this.getAttribute("data-ratio");
      if (attr && /^\d+\/\d+$/.test(attr)) {
        const [w, h] = attr.split("/").map(Number);
        if (w > 0 && h > 0) return w / h;
      }
      return 16 / 9;
    }
    getImageBox() {
      return this.closest(".dl-snippets__imageBox, .dl-snippets_imageBox");
    }

    _ensureLayoutStyles() {
      const parent = this.getImageBox();
      if (parent) {
        const cs = getComputedStyle(parent);
        if (cs.position === "static") parent.style.position = "relative";
        if (cs.overflow !== "hidden") parent.style.overflow = "hidden";
      }
      const selfCS = getComputedStyle(this);
      if (selfCS.display !== "block") this.style.display = "block";
      this.style.width = "100%";
      this.style.height = "100%";
      if (selfCS.position === "static") this.style.position = "relative";

      const playerEl = this.querySelector(".dl-js-vimeo__player");
      if (playerEl) {
        playerEl.style.position = "absolute";
        playerEl.style.top = "50%";
        playerEl.style.left = "50%";
        playerEl.style.transform = "translate(-50%, -50%)";
        playerEl.style.willChange = "width, height, transform";
        const iframe = playerEl.querySelector("iframe");
        if (iframe) {
          iframe.style.width = "100%";
          iframe.style.height = "100%";
          iframe.style.display = "block";
        }
      }
    }

    // “致命的” のみフォールバック
    _criticalFallback(reason = "") {
      if (this._fallbackShown) return;
      this._fallbackShown = true;
      this.showFallbackContent(reason);
    }

    // “軽微エラー”（autoplay拒否・一時失敗など）はログだけ
    _softFail(e, label) {
      // console.warn(`Vimeo soft error${label ? " (" + label + ")" : ""}:`, e);
    }

    // ========= fallback view =========
    showFallbackContent(reason = "") {
      const wrap = this.querySelector(".dl-js-vimeo__player");
      if (wrap) wrap.innerHTML = "";

      const coverUrl = this.getAttribute("data-cover") || "";
      const videoId = this.idVideo || this.getAttribute("data-id");
      const hash = this.getAttribute("data-hash");
      const vimeoPage = videoId ? (hash ? `https://vimeo.com/${videoId}?h=${hash}` : `https://vimeo.com/${videoId}`) : "https://vimeo.com/";

      const link = document.createElement("a");
      link.href = vimeoPage;
      link.target = "_blank";
      link.rel = "noopener";
      link.setAttribute("aria-label", "Open on Vimeo");

      link.style.position = "absolute";
      link.style.inset = "0";
      link.style.display = "block";
      if (coverUrl) {
        link.style.backgroundImage = `url("${coverUrl}")`;
        link.style.backgroundSize = "cover";
        link.style.backgroundPosition = "center";
      } else {
        link.style.display = "grid";
        link.style.placeItems = "center";
        link.style.textDecoration = "none";
        link.style.color = "inherit";
        link.style.background = "var(--Base2, #f3f3f3)";
        link.innerHTML = `<span style="padding:8px 12px;border-radius:6px;background:#000;color:#fff;font-size:14px">Open on Vimeo</span>`;
      }
      wrap?.appendChild(link);

      if (reason) console.warn("[DL_Vimeo] Fallback:", reason);
    }

    // ========= layout (cover) =========
    updateParentVars() {
      const parent = this.getImageBox() || this;
      const playerEl = this.querySelector(".dl-js-vimeo__player");
      if (!parent || !playerEl) return;

      this._ensureLayoutStyles();

      const rect = parent.getBoundingClientRect();
      const containerW = rect.width;
      const containerH = rect.height;
      if (containerW <= 0 || containerH <= 0) return; // レイアウト未確定は黙ってスキップ

      let vrW = parseFloat((parent.style.getPropertyValue("--vr-w") || "").trim());
      let vrH = parseFloat((parent.style.getPropertyValue("--vr-h") || "").trim());
      const videoRatio = vrW > 0 && vrH > 0 ? vrW / vrH : this._aspectFallback();
      const containerRatio = containerW / containerH;

      let videoWidth, videoHeight;
      if (containerRatio > videoRatio) {
        // 親の方が横長 → 横基準拡大（高さオーバー）
        videoWidth = containerW;
        videoHeight = containerW / videoRatio;
      } else {
        // 親の方が縦長 → 縦基準拡大（横オーバー）
        videoHeight = containerH;
        videoWidth = containerH * videoRatio;
      }

      const w = Math.ceil(videoWidth);
      const h = Math.ceil(videoHeight);
      if (playerEl._w !== w) {
        playerEl.style.width = w + "px";
        playerEl._w = w;
      }
      if (playerEl._h !== h) {
        playerEl.style.height = h + "px";
        playerEl._h = h;
      }

      parent.style.setProperty("--width", containerW + "px");
      parent.style.setProperty("--height", containerH + "px");
    }

    updateAspectFromVideoDimensions(retry = 6, delay = 300) {
      if (!this.player) return;
      // 既にアスペクト比を設定済みの場合はスキップ
      if (this._aspectRatioSet) return;

      Promise.all([this.player.getVideoWidth(), this.player.getVideoHeight()])
        .then(([vw, vh]) => {
          if (vw > 0 && vh > 0) {
            const target = this.getImageBox() || this;
            target.style.setProperty("--vr-w", String(vw));
            target.style.setProperty("--vr-h", String(vh));
            this._aspectRatioSet = true; // フラグをセット
            this.updateParentVars();
          } else if (retry > 0) {
            setTimeout(() => this.updateAspectFromVideoDimensions(retry - 1, delay), delay);
          } else {
            // 比率が取れなくてもフォールバックしない：フォールバック比率で描画続行
            this._softFail(new Error("no video dimensions"), "get-dimensions");
            this._aspectRatioSet = true; // 失敗してもフラグをセットして無限ループを防ぐ
          }
        })
        .catch((e) => {
          if (retry > 0) {
            setTimeout(() => this.updateAspectFromVideoDimensions(retry - 1, delay), delay);
          } else {
            this._softFail(e, "get-dimensions");
            this._aspectRatioSet = true; // 失敗してもフラグをセットして無限ループを防ぐ
          }
        });
    }

    // ========= 追加: 品質URL生成（最低720p） & 字幕オフ =========
    _normalizeQuality(requested) {
      // Vimeoの代表的プリセットを昇順に並べ、"最低720p"に底上げ
      const order = ["240p", "360p", "540p", "720p", "1080p", "2k", "4k"];
      const idxMin = order.indexOf("720p");

      const q = (requested || "").toLowerCase().trim();
      const idxReq = order.indexOf(q);
      if (idxReq === -1) return "720p"; // 未指定/不正 → 720p
      return order[Math.max(idxReq, idxMin)]; // 720p未満なら720pに丸め
    }

    _buildVideoUrl(id, hash) {
      // data-quality があれば使うが、最低720pに正規化
      const requested = this.getAttribute("data-quality");
      const q = this._normalizeQuality(requested);
      const base = hash ? `https://player.vimeo.com/video/${id}?h=${hash}` : `https://player.vimeo.com/video/${id}`;
      return base.includes("?") ? `${base}&quality=${encodeURIComponent(q)}` : `${base}?quality=${encodeURIComponent(q)}`;
    }

    _ensureCaptionsOff(retry = 6, delay = 300) {
      if (!this.player || typeof this.player.disableTextTrack !== "function") return;
      Promise.resolve()
        .then(() => this.player.disableTextTrack())
        .catch((e) => {
          if (retry > 0) {
            setTimeout(() => this._ensureCaptionsOff(retry - 1, delay), delay);
          } else {
            this._softFail(e, "disableTextTrack");
          }
        });
    }

    // ========= init =========
    init() {
      const container = this.querySelector(".dl-js-vimeo__player");
      if (!container) {
        console.warn("[DL_Vimeo] .dl-js-vimeo__player not found");
        this._criticalFallback("no-container");
        return;
      }

      this._ensureLayoutStyles();

      const idBase = this.getAttribute("data-id");
      const idSp = this.getAttribute("data-id_sp");
      this.idVideo = idSp && this._media() === "SP" ? idSp : idBase;

      this.mode = (this.getAttribute("data-mode") || "autoplay").toLowerCase();
      this.auto = this._bool(this.getAttribute("data-auto"), true);
      this.controls = this._bool(this.getAttribute("data-controls"), false);
      this.mute = this._bool(this.getAttribute("data-mute"), true);
      this.loop = this._bool(this.getAttribute("data-loop"), true);
      this.cover = this.getAttribute("data-cover") || "";

      const hash = this.getAttribute("data-hash");
      if (!this.idVideo) {
        this._criticalFallback("no-id");
        return;
      }

      // ★ 最低720pを担保したURL
      const videoUrl = this._buildVideoUrl(this.idVideo, hash);

      if (this.mode === "autoplay") {
        this.auto = true;
        this.mute = true;
        this.loop = true;
        this.controls = false;
      } else {
        this.auto = false;
        this.mute = this._bool(this.getAttribute("data-mute"), false);
        this.controls = this._bool(this.getAttribute("data-controls"), true);
      }

      const opts = {
        url: videoUrl,
        autoplay: this.auto ? 1 : 0,
        muted: this.mute ? 1 : 0,
        loop: this.loop ? 1 : 0,
        controls: this.controls ? 1 : 0,
        autopause: 0,
        dnt: 1,
        byline: 0,
        title: 0,
        portrait: 0,
        responsive: 0,
        playsinline: 1,
      };
      if (this.mode === "autoplay" && !this.controls) {
        opts.background = 1;
        opts.muted = 1;
        this.mute = true;
        this.auto = true;
      }

      // プレイヤー生成（致命的ならフォールバック）
      try {
        this.player = new Vimeo.Player(container, opts);
      } catch (e) {
        console.warn("[DL_Vimeo] Vimeo constructor error:", e);
        this._criticalFallback("ctor");
        return;
      }

      // ready() ウォッチドッグ（致命的判断は遅らせる）
      this._readyWatchdogId = setTimeout(() => {
        if (!this._ready) {
          console.warn("[DL_Vimeo] ready() timeout");
          this._criticalFallback("ready-timeout");
        }
      }, 12000);

      this.player
        .ready()
        .then(() => {
          if (this._readyWatchdogId) {
            clearTimeout(this._readyWatchdogId);
            this._readyWatchdogId = null;
          }

          const iframe = this.player.element;
          if (iframe) {
            iframe.setAttribute("playsinline", "");
            iframe.setAttribute("webkit-playsinline", "");
          }
          this._ready = true;

          if (this.mute && this.player.setMuted) {
            this.player.setMuted(true).catch((e) => this._softFail(e, "setMuted"));
          }

          // ★ 字幕オフ（初期化直後）
          this._ensureCaptionsOff();

          // autoplay は失敗してもフォールバックしない（ユーザ操作で再生できる前提）
          if (this.auto) {
            this.player.play().catch((e) => {
              this._softFail(e, "autoplay");
              if (this.mode === "interactive") this._ensureCover();
            });
          }

          // 初回レイアウト & 実寸取得
          this.updateParentVars();
          this.updateAspectFromVideoDimensions();

          // 監視
          const parent = this.getImageBox() || this;
          this._ro = new ResizeObserver(() => this.updateParentVars());
          this._ro.observe(parent);

          // イベント
          this.player.on("ended", () => {
            if (this.loop) this.play();
          });
          this.player.on("pause", () => {
            if (this.mode === "interactive" && this._coverEl) {
              this._coverEl.classList.remove("is-hidden");
              this.classList.remove("is-play");
            }
          });
          this.player.on("play", () => {
            this.classList.add("is-play");
            if (this._coverEl) this._coverEl.classList.add("is-hidden");
          });
          this.player.on("error", (e) => {
            this._softFail(e, "event-error");
          });

          // ★ 動画読み込み完了時（loadVideo含む）も再適用
          this.player.on("loaded", () => {
            this._ensureCaptionsOff();
          });

          if (this.mode === "interactive") this._ensureCover();

          this.onScroll();
        })
        .catch((e) => {
          if (this._readyWatchdogId) {
            clearTimeout(this._readyWatchdogId);
            this._readyWatchdogId = null;
          }
          console.warn("[DL_Vimeo] ready() error:", e);
          this._criticalFallback("ready-error");
        });
    }

    // ========= controls =========
    async play() {
      if (!this._ready || !this.player) return;
      try {
        await this.player.play();
        this.classList.add("is-play");
      } catch (e) {
        this._softFail(e, "play");
      }
    }
    async pause() {
      if (!this._ready || !this.player) return;
      try {
        await this.player.pause();
        this.classList.remove("is-play");
      } catch (e) {
        this._softFail(e, "pause");
      }
    }

    // ========= handlers =========
    onScroll() {
      if (!this._ready) return;
      const rect = this.getBoundingClientRect();
      const scrollTop = pageYOffset || document.documentElement.scrollTop;
      const elTop = rect.top + scrollTop;
      const inView = scrollTop + innerHeight >= elTop && scrollTop <= elTop + rect.height + innerHeight;
      if (this.mode === "autoplay" && this.auto) {
        inView ? this.play() : this.pause();
      }
    }

    onResize() {
      const idBase = this.getAttribute("data-id");
      const idSp = this.getAttribute("data-id_sp");
      const nextId = idSp && this._media() === "SP" ? idSp : idBase;

      if (String(nextId) !== String(this.idVideo)) {
        this.idVideo = nextId;
        const hash = this.getAttribute("data-hash");
        const url = this._buildVideoUrl(this.idVideo, hash); // ★ 最低720pのURLで切替
        if (this.player && this.player.loadVideo) {
          this.player
            .loadVideo({ url })
            .then(() => {
              // 字幕オフ再適用（Vimeoは切替時に内部状態が変わることがある）
              this._ensureCaptionsOff();
              // 動画切り替え時はアスペクト比を再取得
              this._aspectRatioSet = false;
              this.updateAspectFromVideoDimensions();
            })
            .catch((e) => {
              console.warn("[DL_Vimeo] loadVideo error:", e);
              this._criticalFallback("loadVideo");
            });
        }
      }
      this.updateParentVars();
      this.onScroll();
    }

    // ========= cover =========
    _ensureCover() {
      if (this._coverEl) return;
      const cover = document.createElement("button");
      cover.type = "button";
      cover.className = "dl-js-vimeo__cover";
      cover.setAttribute("aria-label", "Play");

      cover.style.position = "absolute";
      cover.style.inset = "0";
      cover.style.backgroundSize = "cover";
      cover.style.backgroundPosition = "center";
      cover.style.border = "0";
      cover.style.cursor = "pointer";
      cover.style.transition = "opacity .2s linear";

      const coverUrl = this.getAttribute("data-cover");
      if (coverUrl) cover.style.backgroundImage = `url("${coverUrl}")`;

      cover.innerHTML = `
        <span class="dl-js-vimeo__cover__overlay" aria-hidden="true" style="position:absolute; inset:0; display:grid; place-items:center;">
          <svg class="dl-js-vimeo__cover__icon" viewBox="0 0 80 80" width="80" height="80" aria-hidden="true">
            <circle cx="40" cy="40" r="38" fill="rgba(0,0,0,0.5)"></circle>
            <polygon points="32,26 60,40 32,54" fill="#fff"></polygon>
          </svg>
        </span>
      `;
      cover.addEventListener("click", this._onCoverClick);
      this.appendChild(cover);
      this._coverEl = cover;
      this._coverEl.classList.remove("is-hidden");
    }

    _isInViewNow(margin = 0) {
      const vh = window.innerHeight || document.documentElement.clientHeight;
      const vw = window.innerWidth || document.documentElement.clientWidth;
      const r = this.getBoundingClientRect();
      return r.bottom > -margin && r.top < vh + margin && r.right > -margin && r.left < vw + margin;
    }

    async onCoverClick() {
      if (!this._ready || !this.player) return;
      try {
        await this.player.setMuted(false);
      } catch (e) {
        this._softFail(e, "unmute");
      }
      try {
        await this.play();
      } catch (e) {
        console.warn("Vimeo player error:", e);
      }
    }
  }

  if (!customElements.get("dl-js-vimeo")) {
    customElements.define("dl-js-vimeo", DL_Vimeo);
  }
})();
